import { DetailModalControls } from './DetailModalControls';
import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';
import GARecordDetails, { MetricCells, MetricHeaders } from './GARecordDetails';
import { filterRecords, scopeSummaryRow, sumMetrics } from './gaReporting';

export function SOLHCNonHC360({ department = [], quarter = [], customRange = null, yearMode = 'current', period = [], category = [], hcType = [], status = [], country = [], subCategory = [], vendorName = [] }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drill, setDrill] = useState(null);
    const [records, setRecords] = useState({ hc: [], nonhc: [], commission: [] });
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    // The Non-HC "expense category" drill-within-a-drill (see GARecordDetails)
    // is lifted up here instead of living as local state inside that
    // component, so the modal header can show a single "back" arrow that
    // pops whichever level is active — vendor list -> expense category list
    // -> close — the same one-hop-back pattern used by every other
    // Employee_Elements drill modal.
    const [expense, setExpense] = useState(null);
    const [showItems, setShowItems] = useState(false);
    const query = useMemo(() => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries({ hc_type: hcType, status, country, sub_category: subCategory, name: vendorName })) {
            if (Array.isArray(value)) { if (value.length) params.set(key, value.join(',')); }
            else if (value) params.set(key, value);
        }
        return params.toString();
    }, [hcType, status, country, subCategory, vendorName]);
    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError('');
        fetch(`${API_URL}/api/sol/summary/?${query}`, { credentials: 'include' })
            .then(res => readApiJson(res, 'Could not load HC & Non-HC 360.'))
            .then(body => { if (!cancelled) setData(body); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [query]);
    useEffect(() => { setDrill(null); }, [department, quarter, customRange, yearMode, period, category, query]);
    useEffect(() => { setExpense(null); setShowItems(false); }, [drill]);
    useEffect(() => {
        if (!drill) return undefined;
        let cancelled = false;
        setDetailsLoading(true); setDetailsError('');
        const sources = drill.source === 'total' ? ['hc', 'nonhc', 'commission'] : [drill.source];
        Promise.all(sources.map(async source => {
            const url = source === 'hc' ? '/api/sol/raw/' : source === 'nonhc' ? '/api/sol/nonhc/raw/' : '/api/sol/commission/raw/';
            const res = await fetch(`${API_URL}${url}`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load detail records.');
            return [source, body.records || []];
        })).then(entries => { if (!cancelled) setRecords(Object.fromEntries(entries)); })
            .catch(err => { if (!cancelled) setDetailsError(err.message); })
            .finally(() => { if (!cancelled) setDetailsLoading(false); });
        return () => { cancelled = true; };
    }, [drill]);
    if (loading) return <p role="status">Loading HC & Non-HC 360...</p>;
    if (error) return <p role="alert">{error}</p>;
    if (!data?.has_data) return <p>{data?.message || 'No data available.'}</p>;
    const isPriorOnlyView = yearMode.includes('prior') && !yearMode.includes('current');
    const currentYear = isPriorOnlyView ? data.prior_year : data.current_year;
    const priorYear = isPriorOnlyView ? '' : data.prior_year;
    const options = { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear: data.prior_year };
    const scope = row => scopeSummaryRow(row, options);
    const metrics = row => ({ actual: row.fy_actual_forecast, aop: row.fy_aop, py: row.fy_prior_year });
    const months = scope(data.sol_total.total).monthly.map(m => m.month);
    const periodIsYtdOnly = period.includes('ytd') && !period.includes('fy');
    const quarterLabel = quarter.join('+');
    const label = `${periodIsYtdOnly ? 'YTD' : 'FY'} ${currentYear}${quarter.length ? ` — ${quarterLabel}` : ''}${customRange ? ` — ${customRange.start} to ${customRange.end}` : ''}${periodIsYtdOnly ? ` — through ${data.as_of || 'no actual months'}` : ''}`;
    // Category (Headcount/Non-Headcount/Commission) is multi-select too — an
    // empty or all-included selection means Total, same convention as every
    // other SOL tab's Category filter.
    const hcExcluded = category.length > 0 && !category.includes('hc');
    const nonhcExcluded = category.length > 0 && !category.includes('nonhc');
    const commissionExcluded = category.length > 0 && !category.includes('commission');
    // What a drill into "Total" should pass as its `source` — mirrors the
    // old single-select category value exactly ('total' whenever more than
    // one, or none, of the three are selected).
    const categorySource = category.length === 1 ? category[0] : 'total';
    // Sub-Category is a Non-HC-only dimension (vendor/expense line items) —
    // there's no such thing as a headcount record's "sub-category", so once
    // that filter narrows the Non-HC rows below, showing an unfiltered HC
    // Cost total alongside them would misleadingly mix a filtered figure
    // with an unfiltered one in the same table. Hide it instead.
    const showHc = !hcExcluded && !subCategory.length && !vendorName.length;
    const showNonhc = !nonhcExcluded;
    // A category with nothing in Actual/AOP/PY for the selected scope (e.g. a
    // Country/Status filter that excludes every record it ever had) is just
    // noise in this table -- dropped unless at least one of its own figures
    // is nonzero (including negative).
    const hasActivity = m => !!(m.actual || m.aop || m.py);
    const hcRows = [];
    if (showHc) (data.categories || []).filter(name => !department.length || department.includes(name)).forEach(name => {
        const row = data.headcount_costs.by_category[name];
        if (!row) return;
        const m = metrics(scope(row));
        if (hasActivity(m)) hcRows.push({ label: name, source: 'hc', category: name, ...m });
    });
    const nonhcRows = [];
    if (showNonhc) (data.categories || []).filter(name => !department.length || department.includes(name)).forEach(name => {
        const row = data.non_headcount_costs.by_category[name];
        if (!row) return;
        const m = metrics(scope(row));
        if (hasActivity(m)) nonhcRows.push({ label: name, source: 'nonhc', category: name, ...m });
    });
    // Commission only exists for the Sales APAC/NAM/Solution dashboards —
    // data.commission_costs is absent for every other department, so this
    // section (and its rows) simply doesn't render there.
    const commissionRows = [];
    if (data.commission_costs && !commissionExcluded && !subCategory.length) (data.categories || []).filter(name => !department.length || department.includes(name)).forEach(name => {
        const row = data.commission_costs.by_category[name];
        if (!row) return;
        const m = metrics(scope(row));
        if (hasActivity(m)) commissionRows.push({ label: name, source: 'commission', category: name, ...m });
    });
    const filteredHc = filterRecords(records.hc, 'hc', { hcType, status, country });
    const filteredNonhc = filterRecords(records.nonhc, 'nonhc', { subCategory, vendorName });
    const filteredCommission = filterRecords(records.commission, 'commission', {});
    const detailRecords = drill?.source === 'total'
        ? [
            ...filteredHc.map(r => ({ ...r, _source: 'hc' })),
            ...filteredNonhc.map(r => ({ ...r, _source: 'nonhc' })),
            ...filteredCommission.map(r => ({ ...r, _source: 'commission' })),
        ]
        : drill?.source === 'hc' ? filteredHc : drill?.source === 'nonhc' ? filteredNonhc : filteredCommission;
    const openDrill = (source, drillCategory, drillLabel) => setDrill({ source, category: drillCategory, label: drillLabel });
    const canGoBack = !!(expense || showItems);
    const goBack = () => { if (showItems) setShowItems(false); else setExpense(null); };
    return <div className="esd">
        {drill && <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) setDrill(null); }}><div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 1100 }}>
            <div className="legal-modal-hdr">
                <div><div className="legal-modal-title">{drill.label}</div><div className="legal-modal-sub">{label}</div></div>
                <DetailModalControls onBack={canGoBack ? goBack : undefined} onClose={() => setDrill(null)} />
            </div>
            <div className="legal-modal-body">
                <GARecordDetails
                    source={drill.source} category={drill.category} records={detailRecords} months={months}
                    currentYear={currentYear} priorYear={priorYear} loading={detailsLoading} error={detailsError}
                    expense={expense} onSelectExpense={setExpense} showItems={showItems} onShowItems={setShowItems}
                />
            </div>
        </div></div>}
        <div className="esd-section"><div className="esd-section-head"><span className="esd-section-title">HC &amp; Non-HC 360 — {label}</span></div>
            <div className="esd-table-wrap"><table className="esd-table"><thead><tr><th>Cost / Expense Category</th><MetricHeaders /></tr></thead>
                <tbody>
                    {showHc && hcRows.map(row => <tr key={`${row.source}-${row.label}`} className="esd-row-cat esd-row-cat--hc" onClick={() => openDrill(row.source, row.category, row.label)}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill(row.source, row.category, row.label); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {showHc && !!hcRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--hc"
                            onClick={() => openDrill('hc', department[0] || null, 'HC Cost Total')}
                        >
                            <td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('hc', department[0] || null, 'HC Cost Total'); }}>HC Cost Total — view details</button></td>
                            <MetricCells row={sumMetrics(hcRows)} />
                        </tr>
                    )}
                    {showNonhc && nonhcRows.map(row => <tr key={`${row.source}-${row.label}`} className="esd-row-cat esd-row-cat--nonhc" onClick={() => openDrill(row.source, row.category, row.label)}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill(row.source, row.category, row.label); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {showNonhc && !!nonhcRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--nonhc"
                            onClick={() => openDrill('nonhc', department[0] || null, 'Non-HC Total')}
                        >
                            <td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('nonhc', department[0] || null, 'Non-HC Total'); }}>Non-HC Total — view details</button></td>
                            <MetricCells row={sumMetrics(nonhcRows)} />
                        </tr>
                    )}
                    {commissionRows.map(row => <tr key={`${row.source}-${row.label}`} className="esd-row-cat esd-row-cat--commission" onClick={() => openDrill(row.source, row.category, row.label)}><td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill(row.source, row.category, row.label); }}>{row.label}</button></td><MetricCells row={row} /></tr>)}
                    {!!commissionRows.length && (
                        <tr
                            className="esd-row-total esd-row-total--commission"
                            onClick={() => openDrill('commission', department[0] || null, 'Commission Total')}
                        >
                            <td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill('commission', department[0] || null, 'Commission Total'); }}>Commission Total — view details</button></td>
                            <MetricCells row={sumMetrics(commissionRows)} />
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr className="esd-row-total esd-row-total--grand" onClick={() => openDrill(categorySource, department[0] || null, 'Total')}>
                        <td><button className="esd-detail-link" type="button" onClick={e => { e.stopPropagation(); openDrill(categorySource, department[0] || null, 'Total'); }}>Total — view details</button></td>
                        <MetricCells row={sumMetrics([...hcRows, ...nonhcRows, ...commissionRows])} />
                    </tr>
                </tfoot>
            </table></div>
        </div>
    </div>;
}
export default SOLHCNonHC360;
